import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';

/**
 * One search box with a suggestion list, the ARIA combobox way (WAI-ARIA APG "list
 * autocomplete"): the input keeps focus, ArrowUp/ArrowDown move a highlight over the options
 * (aria-activedescendant), Enter opens the highlighted option (no highlight: the form submits),
 * Escape closes the list and keeps the text — the native type=search Escape cleared it. Used by
 * the homepage's search box and the search overlay (src/pages/Home.tsx,
 * src/components/SearchOverlay.tsx).
 *
 * `dismissable` (the homepage box, whose list floats over the page): the list closes on a press
 * outside it and when focus leaves the box (checked a tick later, so a tap on an option is never
 * lost — and pressing an option keeps focus in the input), and reopens on focus or typing.
 */
export function useCombobox({
  count,
  onPick,
  onEscape,
  dismissable = false,
}: {
  /** how many options the list shows right now */
  count: number;
  /** open option i (Enter on the highlight) */
  onPick: (i: number) => void;
  /** Escape with the list already closed (the overlay closes itself) */
  onEscape?: () => void;
  dismissable?: boolean;
}) {
  const base = useId().replace(/:/g, '');
  const listId = `${base}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const optionId = (i: number) => `${base}-opt-${i}`;
  const expanded = open && count > 0;

  // a new list, or a list opened again, starts with nothing highlighted
  useEffect(() => setActive(-1), [count]);
  useEffect(() => {
    if (!open) setActive(-1);
  }, [open]);

  // the homepage box: a press anywhere outside closes the list
  useEffect(() => {
    if (!dismissable || !open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [dismissable, open]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        if (count) setActive((a) => (a + 1 >= count ? 0 : a + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
        if (count) setActive((a) => (a <= 0 ? count - 1 : a - 1));
      } else if (e.key === 'Enter') {
        if (expanded && active >= 0 && active < count) {
          e.preventDefault();
          onPick(active);
        }
      } else if (e.key === 'Escape') {
        // never the browser's own Escape on type=search: it clears what was typed
        e.preventDefault();
        if (expanded) {
          setOpen(false);
          setActive(-1);
        } else onEscape?.();
      } else if (e.key === 'Tab') {
        // Tab goes on to the Search button; the options are reached with the arrow keys
        if (dismissable) setOpen(false);
      } else if (e.key === 'Home' || e.key === 'End') {
        setActive(-1);
      }
    },
    [active, count, dismissable, expanded, onEscape, onPick]
  );

  const onBlur = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      if (!dismissable) return;
      // a tick later: a press on an option has kept focus (onMouseDown below), a press elsewhere has not
      setTimeout(() => {
        if (rootRef.current && !rootRef.current.contains(document.activeElement)) setOpen(false);
      }, 0);
    },
    [dismissable]
  );

  return {
    /** the list is showing options */
    open: expanded,
    /** the box wants its panel open (focused, typing) — a notice may show without options */
    wanted: open,
    setOpen,
    active,
    setActive,
    rootRef,
    inputProps: {
      role: 'combobox' as const,
      'aria-expanded': expanded,
      'aria-controls': listId,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': expanded && active >= 0 ? optionId(active) : undefined,
      onKeyDown,
      onBlur,
      onFocus: () => setOpen(true),
    },
    listProps: {
      id: listId,
      role: 'listbox' as const,
      // pressing an option keeps focus in the input (Safari and iOS never focus a tapped link)
      onMouseDown: (e: MouseEvent) => e.preventDefault(),
    },
    optionProps: (i: number) => ({
      id: optionId(i),
      role: 'option' as const,
      'aria-selected': i === active,
      tabIndex: -1,
      onMouseMove: () => setActive(i),
    }),
  };
}

/** "8 results", said 400 ms after the list stops changing (an sr-only role=status line). */
export function useSettledCount(n: number, active: boolean, delay = 400): number | null {
  const [said, setSaid] = useState<number | null>(null);
  useEffect(() => {
    if (!active) {
      setSaid(null);
      return;
    }
    const t = setTimeout(() => setSaid(n), delay);
    return () => clearTimeout(t);
  }, [n, active, delay]);
  return said;
}
